import styled from 'styled-components';

// ─── Main container ────────────────────────────────────────────────────────

export const Container = styled.div`
	width: 100%;
	max-width: 1100px;
	margin: 0 auto;
`;

// ─── Header ────────────────────────────────────────────────────────────────

export const HeaderRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 24px;
	flex-wrap: wrap;
	gap: 12px;
`;

export const BackButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 8px 16px;
	background: white;
	border: 1px solid #ddd;
	border-radius: 6px;
	cursor: pointer;
	font-size: 0.9rem;
	color: #555;
	transition: all 0.2s ease;

	&:hover {
		background: #f5f5f5;
		border-color: #ccc;
	}
`;

export const Title = styled.h1`
	margin: 0;
	font-size: 1.6rem;
	color: #2c3e50;
`;

export const Subtitle = styled.p`
	margin: 4px 0 0;
	color: #888;
	font-size: 0.95rem;
`;

// ─── Summary cards row ─────────────────────────────────────────────────────

export const SummaryGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
	gap: 16px;
	margin-bottom: 32px;
`;

export const SummaryCard = styled.div`
	background: white;
	border-radius: 10px;
	padding: 20px;
	text-align: center;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
	border: 1px solid #f0f0f0;
`;

export const SummaryLabel = styled.div`
	font-size: 0.75rem;
	color: #888;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 6px;
`;

export const SummaryValue = styled.div`
	font-size: 2rem;
	font-weight: 700;
	color: #2c3e50;
`;

export const SummarySub = styled.div`
	font-size: 0.8rem;
	color: #aaa;
	margin-top: 4px;
`;

// ─── Album cards list ──────────────────────────────────────────────────────

export const AlbumList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

export const AlbumCard = styled.div`
	background: white;
	border-radius: 10px;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
	border: 1px solid #f0f0f0;
	padding: 20px 24px;
	display: flex;
	gap: 20px;
	align-items: stretch;
	cursor: pointer;
	transition: box-shadow 0.2s ease, transform 0.15s ease;

	&:hover {
		box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
		transform: translateY(-1px);
	}

	@media (max-width: 768px) {
		flex-direction: column;
		padding: 16px;
	}
`;

export const Thumbnail = styled.div<{ $imageUrl?: string }>`
	width: 140px;
	min-height: 100px;
	border-radius: 6px;
	background: ${({ $imageUrl }) =>
		$imageUrl
			? `url(${$imageUrl}) center/cover no-repeat`
			: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	color: white;
	font-size: 2rem;

	@media (max-width: 768px) {
		width: 100%;
		height: 120px;
	}
`;

export const AlbumInfo = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: 8px;
	min-width: 0;
`;

export const AlbumName = styled.h3`
	margin: 0;
	font-size: 1.1rem;
	color: #2c3e50;
	font-weight: 600;
	line-height: 1.3;
`;

export const AlbumMeta = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 8px 16px;
	font-size: 0.85rem;
	color: #888;
`;

export const Chip = styled.span`
	display: inline-flex;
	align-items: center;
	gap: 4px;
`;

// ─── Breakdown section inside each card ────────────────────────────────────

export const BreakdownSection = styled.div`
	margin-top: 4px;
`;

export const BreakdownLabel = styled.div`
	font-size: 0.75rem;
	color: #999;
	text-transform: uppercase;
	letter-spacing: 0.4px;
	margin-bottom: 6px;
`;

export const UnitatRow = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 3px 0;
`;

export const UnitatBadge = styled.span<{ $unitat: string }>`
	display: inline-block;
	background: ${({ $unitat }) => {
		const colors: Record<string, string> = {
			cills: '#e74c3c',
			llids: '#3498db',
			rings: '#2ecc71',
			pics: '#f39c12',
			trucs: '#9b59b6',
			agrupament: '#1abc9c',
			varis: '#95a5a6',
		};
		return colors[$unitat] || '#95a5a6';
	}};
	color: white;
	padding: 2px 8px;
	border-radius: 4px;
	font-size: 0.75rem;
	font-weight: 600;
	min-width: 60px;
	text-align: center;
`;

export const WeightBarContainer = styled.div`
	flex: 1;
	background: #e9ecef;
	border-radius: 4px;
	height: 8px;
`;

export const WeightBar = styled.div<{ $width: number; $color: string }>`
	height: 8px;
	border-radius: 4px;
	background: ${({ $color }) => $color};
	width: ${({ $width }) => Math.max($width, 2)}%;
	min-width: 2px;
	transition: width 0.3s ease;
`;

export const WeightValue = styled.span`
	font-size: 0.8rem;
	font-weight: 600;
	color: #555;
	min-width: 32px;
	text-align: right;
`;

export const YearChipsContainer = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	margin-top: 6px;
`;

export const YearChip = styled.span`
	background: #e8f4fd;
	color: #2c6b9e;
	padding: 3px 10px;
	border-radius: 10px;
	font-size: 0.8rem;
	font-weight: 500;
	border: 1px solid #b8d4ed;
	display: inline-flex;
	align-items: center;
	gap: 4px;
`;

export const YearChipCount = styled.span`
	font-weight: 700;
	color: #1a5276;
`;

// ─── Arrow / CTA ───────────────────────────────────────────────────────────

export const ViewArrow = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 1.5rem;
	color: #ccc;
	flex-shrink: 0;
	width: 40px;
	transition: color 0.2s ease;

	${AlbumCard}:hover & {
		color: #3498db;
	}

	@media (max-width: 768px) {
		display: none;
	}
`;

// ─── Hide / show controls ─────────────────────────────────────────────────

export const ShowAllButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	padding: 6px 14px;
	background: #fff3cd;
	border: 1px solid #ffc107;
	border-radius: 6px;
	cursor: pointer;
	font-size: 0.85rem;
	color: #856404;
	transition: all 0.2s ease;

	&:hover {
		background: #ffe69c;
	}
`;

export const HideButton = styled.button`
	position: absolute;
	top: 8px;
	right: 8px;
	width: 28px;
	height: 28px;
	padding: 0;
	background: rgba(0, 0, 0, 0.45);
	border: none;
	border-radius: 50%;
	cursor: pointer;
	font-size: 0.85rem;
	color: white;
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 0;
	transition: opacity 0.2s ease, background 0.2s ease;
	z-index: 2;

	&:hover {
		background: rgba(200, 50, 50, 0.8);
	}
`;

export const AlbumCardWrapper = styled.div<{ $hidden?: boolean }>`
	position: relative;

	${({ $hidden }) =>
		$hidden &&
		`
		display: none;
	`}

	&:hover ${HideButton} {
		opacity: 1;
	}
`;

// ─── Empty state ───────────────────────────────────────────────────────────

export const EmptyState = styled.div`
	background: white;
	border-radius: 10px;
	padding: 48px 24px;
	text-align: center;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
	color: #888;
	font-size: 1rem;
	line-height: 1.6;
`;

export const EmptyIcon = styled.div`
	font-size: 3rem;
	margin-bottom: 12px;
`;

// ─── Loading ───────────────────────────────────────────────────────────────

export const LoadingContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 60px 0;
`;
